/**
 * DemoBookingModal — a reusable "Book a Demo" lead-capture modal used
 * across the /business/drive and /business/pitcrew marketing pages.
 *
 * Captures: name, phone, email, business name, location.
 * Submits to POST /api/public/demo-leads with `source` = "drive" | "pitcrew".
 *
 * Props:
 *   - open: boolean — controlled visibility
 *   - onClose(): void — invoked when user closes / after success
 *   - source: "drive" | "pitcrew" — determines which brand the lead is for
 *   - accent: Tailwind classes for the primary button (e.g. "bg-orange-500 hover:bg-orange-600")
 *   - title: string — modal heading
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { X, Calendar, CheckCircle2, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY = { name: '', phone: '', email: '', business_name: '', location: '' };

export default function DemoBookingModal({ open, onClose, source, accent, title }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState(false);
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setErr('');
      setOk(false);
      // autofocus after paint
      setTimeout(() => firstInputRef.current?.focus(), 60);
    }
  }, [open]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/api/public/demo-leads`, { ...form, source });
      setOk(true);
    } catch (ex) {
      const detail = ex?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(', ') : (detail || 'Something went wrong. Please try again.');
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="demo-booking-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Book a Demo</div>
            <h2 className="text-lg font-extrabold text-zinc-900">{title || 'Tell us about you'}</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="demo-booking-close"
            className="p-1.5 rounded-lg text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {ok ? (
          <div className="p-6 text-center" data-testid="demo-booking-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <div className="mt-4 text-xl font-extrabold text-zinc-900">Thanks, we&apos;ll be in touch!</div>
            <p className="mt-1 text-sm text-zinc-500">Our team typically responds within 1 business day.</p>
            <button
              onClick={onClose}
              className={`mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold ${accent || 'bg-zinc-900 hover:bg-zinc-800'}`}
              data-testid="demo-booking-done"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-3">
            <Field label="Your name">
              <input
                ref={firstInputRef}
                type="text"
                required
                value={form.name}
                onChange={set('name')}
                placeholder="e.g. Ravi Kumar"
                data-testid="demo-field-name"
                className="w-full px-3 py-2.5 rounded-lg bg-white border border-zinc-300 focus:border-zinc-900 outline-none text-sm"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone number">
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={set('phone')}
                  placeholder="+91 98xxx xxxxx"
                  data-testid="demo-field-phone"
                  className="w-full px-3 py-2.5 rounded-lg bg-white border border-zinc-300 focus:border-zinc-900 outline-none text-sm"
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={set('email')}
                  placeholder="you@business.com"
                  data-testid="demo-field-email"
                  className="w-full px-3 py-2.5 rounded-lg bg-white border border-zinc-300 focus:border-zinc-900 outline-none text-sm"
                />
              </Field>
            </div>
            <Field label="Business name">
              <input
                type="text"
                required
                value={form.business_name}
                onChange={set('business_name')}
                placeholder="e.g. ABC Motors"
                data-testid="demo-field-business"
                className="w-full px-3 py-2.5 rounded-lg bg-white border border-zinc-300 focus:border-zinc-900 outline-none text-sm"
              />
            </Field>
            <Field label="Location">
              <input
                type="text"
                required
                value={form.location}
                onChange={set('location')}
                placeholder="City, State"
                data-testid="demo-field-location"
                className="w-full px-3 py-2.5 rounded-lg bg-white border border-zinc-300 focus:border-zinc-900 outline-none text-sm"
              />
            </Field>

            {err && (
              <div className="text-xs font-semibold text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="demo-booking-error">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="demo-booking-submit"
              className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white text-sm font-bold transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${accent || 'bg-zinc-900 hover:bg-zinc-800'}`}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
              {busy ? 'Sending…' : 'Book my demo'}
            </button>
            <p className="text-[11px] text-zinc-500 text-center">
              By submitting you agree to be contacted by our team about Propitstop products.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-zinc-700 mb-1">{label}</div>
      {children}
    </label>
  );
}
